import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, QrCode, Utensils, BarChart3, Store, Shield, ChevronRight, Sparkles } from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen bg-brand-cream" data-testid="landing-page">
      {/* Nav */}
      <header className="sticky top-0 z-40 bg-brand-cream/80 backdrop-blur-md border-b border-border/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Logo size={52} />
          <nav className="hidden md:flex items-center gap-8 text-sm font-semibold text-foreground/80">
            <a href="#features" className="hover:text-foreground transition">Features</a>
            <a href="#pricing" className="hover:text-foreground transition">Pricing</a>
            <a href="#how" className="hover:text-foreground transition">How it works</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="nav-login-link" className="text-sm font-semibold text-foreground/80 hover:text-foreground">Login</Link>
            <Link to="/signup" data-testid="nav-signup-cta">
              <Button className="bg-brand-orange hover:bg-[#E65C00] text-white rounded-full px-5">Get started</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden hero-grid">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <Badge className="bg-brand-navy/5 text-brand-navy border border-brand-navy/10 rounded-full px-3 py-1 mb-6 hover:bg-brand-navy/5">
              <Sparkles className="w-3.5 h-3.5 mr-1.5 brand-orange" />
              Plans from ₹1,000 / year
            </Badge>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">
              Your restaurant menu,
              <br />
              <span className="brand-orange">one QR code away.</span>
            </h1>
            <p className="mt-6 text-lg text-foreground/70 max-w-xl leading-relaxed">
              MenuMojo lets you build, edit and share a beautiful digital menu in minutes.
              Update prices instantly, hide out-of-stock dishes, and let diners scan a branded
              QR to order — no app, no friction.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link to="/signup" data-testid="hero-cta-primary">
                <Button className="bg-brand-orange hover:bg-[#E65C00] text-white rounded-full h-12 px-7 text-base font-semibold">
                  Start your free trial <ChevronRight className="w-4 h-4" />
                </Button>
              </Link>
              <a href="#pricing" data-testid="hero-cta-secondary">
                <Button variant="outline" className="rounded-full h-12 px-7 text-base font-semibold border-brand-navy/20">
                  See pricing
                </Button>
              </a>
            </div>
            <div className="mt-10 flex items-center gap-6 text-sm text-foreground/60">
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-veg" /> 2 minute setup</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-veg" /> Unlimited menu items</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-veg" /> Cancel anytime</span>
            </div>
          </div>

          <div className="lg:col-span-5 relative animate-fade-up" style={{ animationDelay: "150ms" }}>
            <div className="absolute -top-10 -right-8 w-64 h-64 bg-brand-orange/10 rounded-full blur-3xl" />
            <div className="absolute -bottom-8 -left-4 w-56 h-56 bg-brand-navy/10 rounded-full blur-3xl" />
            <div className="relative bg-white border border-border rounded-3xl shadow-2xl shadow-brand-navy/10 p-6">
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1565958011703-44f9829ba187?auto=format&fit=crop&w=900&q=80"
                  alt="Plated dish"
                  className="w-full h-48 object-cover"
                />
              </div>
              <div className="mt-4 flex items-start gap-3">
                <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center shrink-0">
                  <QrCode className="w-6 h-6 brand-orange" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] font-bold text-foreground/50">Today&rsquo;s special</div>
                  <div className="font-heading font-bold text-xl mt-1">Truffle Mushroom Risotto</div>
                  <div className="text-sm text-foreground/60 mt-1">Slow-cooked arborio, wild mushrooms, black truffle oil.</div>
                  <div className="mt-2 font-heading font-bold brand-orange text-lg">₹ 480</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-orange mb-3">Everything you need</div>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight">Run your menu like a pro — without the dev team.</h2>
        </div>
        <div className="mt-14 grid md:grid-cols-12 gap-6">
          {[
            { icon: Utensils, title: "Visual menu builder", desc: "Create categories, sub-categories and dishes with photos, videos, veg/non-veg tags and live pricing.", span: "md:col-span-7" },
            { icon: QrCode, title: "Branded QR codes", desc: "One-click download as PNG or PDF with a polished Powered by MenuMojo footer.", span: "md:col-span-5" },
            { icon: Store, title: "Your brand, your menu", desc: "Upload your logo, cover photo and choose your currency. Customer view is mobile-first.", span: "md:col-span-5" },
            { icon: BarChart3, title: "Real-time updates", desc: "Mark a dish out-of-stock and it vanishes from every live menu instantly.", span: "md:col-span-7" },
            { icon: Shield, title: "Multi-tenant secure", desc: "Each restaurant is isolated. Super admin controls usage across your entire platform.", span: "md:col-span-12" },
          ].map((f, i) => (
            <div key={f.title} className={`${f.span} bg-white border border-border rounded-2xl p-8 shadow-sm hover:shadow-md transition animate-fade-up`} style={{ animationDelay: `${i * 60}ms` }}>
              <div className="w-11 h-11 rounded-xl bg-brand-navy text-white flex items-center justify-center mb-5">
                <f.icon className="w-5 h-5" />
              </div>
              <h3 className="font-heading font-semibold text-xl">{f.title}</h3>
              <p className="text-foreground/60 mt-2 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="relative bg-brand-navy text-white py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-orange mb-3">Pricing</div>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight">Simple, per-outlet plans.</h2>
            <p className="text-white/70 mt-4">
              Pay annually per outlet, or grab a one-off Event/Party pack for a 10-day pop-up menu.
              All plans include branded QR codes, unlimited items and real-time updates.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
            {[
              { id: "restaurant_1", label: "1 Outlet", price: 1000, outlets: "1 outlet" },
              { id: "restaurant_2", label: "2 Outlets", price: 1800, outlets: "2 outlets", highlight: true },
              { id: "restaurant_3_5", label: "3 – 5 Outlets", price: 2500, outlets: "Up to 5 outlets" },
              { id: "restaurant_6_10", label: "6 – 10 Outlets", price: 5000, outlets: "Up to 10 outlets" },
            ].map((p) => (
              <div
                key={p.id}
                className={`relative bg-white text-foreground rounded-2xl p-6 flex flex-col shadow-xl ${
                  p.highlight ? "ring-2 ring-brand-orange" : ""
                }`}
                data-testid={`landing-plan-${p.id}`}
              >
                {p.highlight && (
                  <Badge className="absolute -top-3 left-6 bg-brand-orange text-white hover:bg-brand-orange rounded-full px-3 py-0.5 text-[10px] font-bold uppercase tracking-[0.15em]">
                    Most popular
                  </Badge>
                )}
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-orange">{p.label}</div>
                <div className="mt-3 font-heading font-extrabold text-4xl brand-navy">₹{p.price.toLocaleString("en-IN")}</div>
                <div className="text-xs text-foreground/50 mt-1">per year · {p.outlets}</div>
                <ul className="mt-5 space-y-2 text-sm flex-1">
                  {[
                    "Unlimited menu items",
                    "Branded QR (PNG + PDF)",
                    "Real-time updates",
                    "Dietary & availability tags",
                    "Excel bulk import",
                  ].map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-veg shrink-0 mt-0.5" /> <span className="text-foreground/80">{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/signup" className="mt-6 block">
                  <Button className="w-full bg-brand-orange hover:bg-[#E65C00] text-white rounded-full">
                    Get started
                  </Button>
                </Link>
              </div>
            ))}
          </div>

          {/* Event pack + contact sales */}
          <div className="mt-10 grid md:grid-cols-2 gap-5">
            <div className="bg-gradient-to-br from-brand-orange/15 to-white/5 border border-white/10 rounded-2xl p-6 flex items-center gap-5" data-testid="landing-plan-event_party">
              <div className="w-14 h-14 rounded-xl bg-brand-orange/20 flex items-center justify-center shrink-0">
                <Sparkles className="w-7 h-7 brand-orange" />
              </div>
              <div className="flex-1">
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-orange">Event / Party</div>
                <div className="font-heading font-bold text-2xl mt-1">
                  ₹49 <span className="text-white/60 text-sm font-medium">· 10 days validity</span>
                </div>
                <div className="text-sm text-white/60 mt-1">One menu for pop-ups, weddings, pool parties — expires automatically.</div>
              </div>
              <Link to="/signup">
                <Button className="bg-white text-brand-navy hover:bg-white/90 rounded-full">Get event menu</Button>
              </Link>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 flex items-center gap-5" data-testid="landing-plan-contact">
              <div className="w-14 h-14 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                <Store className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1">
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-orange">10+ Outlets</div>
                <div className="font-heading font-bold text-2xl mt-1">Custom pricing</div>
                <div className="text-sm text-white/60 mt-1">Volume discounts, priority support & dedicated onboarding.</div>
              </div>
              <a href="mailto:info@menumojo.in?subject=MenuMojo%20Enterprise%20Plan">
                <Button variant="outline" className="rounded-full border-white/30 text-white bg-transparent hover:bg-white/10">
                  Contact sales
                </Button>
              </a>
            </div>
          </div>

          <p className="text-xs text-white/50 mt-8 text-center">
            Secure payments via Razorpay · GST invoices on request · Cancel anytime.
          </p>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid md:grid-cols-3 gap-8">
          {[
            { step: "01", title: "Create your account", desc: "Sign up, add your restaurant details and pick your currency." },
            { step: "02", title: "Build your menu", desc: "Add categories, upload dishes, set prices and dietary tags." },
            { step: "03", title: "Print your QR", desc: "Download your branded QR and place it on tables. Done." },
          ].map((s) => (
            <div key={s.step} className="border-t border-brand-navy pt-6">
              <div className="font-heading font-extrabold brand-orange text-5xl">{s.step}</div>
              <h3 className="font-heading font-semibold text-xl mt-4">{s.title}</h3>
              <p className="text-foreground/60 mt-2">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="bg-brand-orange rounded-3xl p-12 md:p-16 flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="text-white max-w-xl">
            <h2 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight">Ready to modernize your menu?</h2>
            <p className="mt-3 text-white/90">Join restaurants using MenuMojo to delight diners with a seamless digital experience.</p>
          </div>
          <Link to="/signup" data-testid="footer-cta">
            <Button className="bg-white text-brand-navy hover:bg-white/90 rounded-full h-12 px-8 font-semibold">
              Get started
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-border py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Logo size={28} />
          <div className="text-xs text-foreground/50">© 2026 MenuMojo — Digital QR Code Menu Manager</div>
        </div>
      </footer>
    </div>
  );
}
